/**
 * Sync-frequency (customDailyFetchCount) and scheduled-sync-time
 * (customDailyFetchTime) constants and helpers for the Galaxy integrations page
 * (GLX-1609, GLX-1656).
 *
 * The UI lets a Mosaic admin change how many times per day an integration fetches
 * data and pin the UTC times those syncs run at. Customers are approved for up to
 * 3 custom daily syncs by default; a 4th or more requires prior approval (surfaced
 * as a note in the modal). The ceiling of 6 is a product/UX guardrail — the
 * Integration Server independently caps the *effective* daily fetch count at 10
 * when scheduling cron jobs. These bounds are mirrored by the galaxy-server
 * `UpdateSyncFrequencyDto`.
 */

export const MIN_SYNC_FREQUENCY = 1;
export const MAX_SYNC_FREQUENCY = 6;

/** Counts at or above this require prior customer approval. */
export const SYNC_FREQUENCY_APPROVAL_THRESHOLD = 4;

/**
 * Effective default when neither `customDailyFetchCount` nor
 * `customDailyFetchTime` is set: the IS auto-schedules 3 fetches per day, at
 * times it spreads across all integrations (so they are not stable or knowable
 * per integration). Shown to the admin so an unset integration does not look
 * like "0 syncs/day".
 */
export const DEFAULT_SYNC_FREQUENCY = 3;

// Phrased around the daily sync count: the approval threshold applies to the
// count alone (a 4th daily sync needs approval even with every time slot left
// empty), not to how many times are pinned.
export const SYNC_FREQUENCY_APPROVAL_NOTE =
  'Customers are approved for up to 3 daily syncs by default. A 4th daily sync or more requires approval first.';

/**
 * The IS cron scheduler only honors the first 4 `customDailyFetchTime` anchors
 * (it does `customDailyFetchTime.slice(0, 4)`); when the count is higher, the
 * remaining syncs are auto-scheduled into the largest gaps between anchors.
 */
export const MAX_SCHEDULED_SYNC_TIMES = 4;

/**
 * 24h zero-padded UTC "HH:mm" — the only format the IS accepts. It strict-parses
 * anchors with `moment(t, 'HH:mm', true)` at scheduling time and silently drops
 * invalid entries (Sentry alert only, no API error), so the format must be
 * enforced up front.
 */
export const SYNC_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

/** True when `value` is an integer within the allowed [MIN, MAX] range. */
export const isValidSyncFrequency = (value: number): boolean =>
  Number.isInteger(value) && value >= MIN_SYNC_FREQUENCY && value <= MAX_SYNC_FREQUENCY;

/** True when the chosen count needs prior customer approval (a 4th sync or more). */
export const requiresSyncFrequencyApproval = (value: number): boolean =>
  value >= SYNC_FREQUENCY_APPROVAL_THRESHOLD;

/** True when `value` is a 24h zero-padded "HH:mm" time (e.g. "08:30", "23:59"). */
export const isValidSyncTime = (value: string): boolean => SYNC_TIME_PATTERN.test(value);

/**
 * Normalize a stored `customDailyFetchTime` setting for editing: the IS stores
 * either a single "HH:mm" string (legacy) or an array. Mirrors the IS scheduler
 * exactly — slice to the honored anchors first, then drop invalid entries — so
 * the modal edits the same times the IS actually runs.
 */
export const normalizeSyncTimes = (value?: string | string[] | null): string[] => {
  const times = Array.isArray(value) ? value : value != null ? [value] : [];
  return times
    .slice(0, MAX_SCHEDULED_SYNC_TIMES)
    .filter((time) => typeof time === 'string' && isValidSyncTime(time));
};

/**
 * Render a UTC "HH:mm" sync time in the admin's local time, so they can sanity
 * check what they are scheduling. Returns null for values that are not valid
 * sync times yet (e.g. a partially-typed input).
 */
export const formatUtcSyncTimeAsLocal = (time: string): string | null => {
  if (!isValidSyncTime(time)) return null;
  const [hours, minutes] = time.split(':').map(Number);
  const date = new Date();
  date.setUTCHours(hours, minutes, 0, 0);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};
